import React, { useState, useEffect } from "react";
import axios from "axios";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

const PDFDiagnostics = ({ jwt }) => {
  const [taskId, setTaskId] = useState("");
  const [filename, setFilename] = useState("");
  const [created, setCreated] = useState("");
  const [outputFormat, setOutputFormat] = useState("html");
  const [diagnostics, setDiagnostics] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("pre"); // "post" = translated, "pre" = pre-translation

  // Fetch latest job on mount
  useEffect(() => {
    const fetchLatestJob = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/admin/latest_pdf_job`,
          { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
        );
        if (res.data && res.data.task_id) {
          setTaskId(res.data.task_id);
          setFilename(res.data.filename || "");
          setCreated(res.data.created || "");
        } else {
          setTaskId("");
          setFilename("");
          setCreated("");
        }
      } catch (err) {
        setTaskId("");
        setFilename("");
        setCreated("");
        setError(
          err.response?.data || err.message || "Could not fetch latest job."
        );
      }
    };
    if (jwt) fetchLatestJob();
  }, [jwt]);

  // Reset diagnostics and error if mode or output format changes
  useEffect(() => {
    setDiagnostics("");
    setError("");
  }, [mode, outputFormat]);

  const handleFetchDiagnostics = async () => {
    setLoading(true);
    setDiagnostics("");
    setError("");
    try {
      const res = await axios.get(
        `${API_BASE}/debug/pdf_docx_report_for_task`,
        {
          params: {
            task_id: taskId,
            output_format: outputFormat,
            mode, // "pre" or "post"
          },
          headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
        }
      );
      setDiagnostics(res.data);
    } catch (err) {
      setError(
        err.response?.data || err.message || "Failed to load diagnostics"
      );
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto my-10 bg-white rounded-xl shadow-md p-6 border">
      <h1 className="text-xl font-bold mb-4 text-center text-black">PDF File Processing Diagnostics</h1>
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:space-x-4">
        <div className="flex-1 mb-2 md:mb-0">
          <span className="font-semibold">Last PDF Translation:</span>{" "}
          {created && created !== "null" && created !== "" ? (
            <span className="text-black">
              {new Date(created).toLocaleString()}
            </span>
          ) : (
            <span className="text-gray-500">No recent PDF translation found.</span>
          )}
        </div>
        <select
          value={mode}
          onChange={e => setMode(e.target.value)}
          className="border p-2 rounded mb-2 md:mb-0"
        >
          <option value="pre">Pre-Translation</option>
          <option value="post">Post-Translation</option>
        </select>
        <select
          value={outputFormat}
          onChange={e => setOutputFormat(e.target.value)}
          className="border p-2 rounded mb-2 md:mb-0"
        >
          <option value="html">View as Table</option>
          <option value="csv">Download CSV</option>
        </select>
        <button
          className="ml-0 md:ml-4 text-white px-4 py-2 rounded hover:opacity-90"
          style={{ backgroundColor: "#00a7e1" }}
          onClick={handleFetchDiagnostics}
          disabled={loading || !taskId}
        >
          {loading ? "Loading..." : "Run Diagnostics"}
        </button>
      </div>
      {error && (
        <div className="bg-red-100 text-red-700 p-2 rounded mb-4">{error}</div>
      )}
      {diagnostics && outputFormat === "html" && (
        <div
          className="overflow-auto border rounded p-2"
          style={{ maxHeight: "700px" }}
          dangerouslySetInnerHTML={{ __html: diagnostics }}
        />
      )}
      {diagnostics && outputFormat === "csv" && (
        <div className="mt-4">
          <a
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(
              diagnostics
            )}`}
            download={`pdf_diagnostics_${taskId}_${mode}.csv`}
            className="underline"
            style={{ color: "#00a7e1" }}
          >
            Download CSV
          </a>
        </div>
      )}
      <div className="text-xs text-gray-500 mt-4">
        <p>
          <b>Pre-Translation:</b> Compares the PDF to the English DOCX created by PDFRest (before translation).<br/>
          <b>Post-Translation:</b> Compares the PDF to the translated DOCX (final output).
        </p>
      </div>
    </div>
  );
};

export default PDFDiagnostics;
